import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import {
  Clock3,
  Database,
  HardDrive,
  ImageOff,
  MapPinned,
  Music2,
  Pencil,
  RotateCcw,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import {
  Button,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  Skeleton,
} from "../components/ui";
import { apiRequest, jsonBody, queryString } from "../lib/api";
import { isLocalCheckoutHostname } from "../lib/checkout-address";
import type {
  AddressSearchUsageSnapshot,
  MapApiUsageSummary,
} from "../../shared/address-search-usage";
import {
  blobToBase64,
  optimizeProductImage,
  type OptimizedImage,
} from "../lib/images";
import {
  normalizeCollection,
  type ApiRecord,
  type SessionUser,
} from "../lib/types";

const get = (item: ApiRecord, ...keys: string[]) =>
  keys.map((key) => item[key]).find((value) => value !== undefined && value !== null);
const asRecord = (input: unknown): ApiRecord =>
  input && typeof input === "object" && !Array.isArray(input)
    ? (input as ApiRecord)
    : {};
const asText = (input: unknown, fallback = "-") =>
  typeof input === "string" || typeof input === "number"
    ? String(input)
    : fallback;
const asNumber = (input: unknown) =>
  Number.isFinite(Number(input)) ? Number(input) : 0;
const MEDIA_PAGE_SIZE = 50;

function megabytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

export function MediaManagerPage({ role }: { role: SessionUser["role"] }) {
  const endpoint = role === "OWNER" ? "/api/owner/media" : "/api/admin/media";
  const [items, setItems] = useState<ApiRecord[]>([]);
  const [storage, setStorage] = useState<ApiRecord>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [optimized, setOptimized] = useState<OptimizedImage>();
  const [preview, setPreview] = useState<string>();
  const [mediaType, setMediaType] = useState("product");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [deleteTarget, setDeleteTarget] = useState<ApiRecord>();
  const [audioDeleteTarget, setAudioDeleteTarget] = useState<ApiRecord>();
  const [audioItems, setAudioItems] = useState<ApiRecord[]>([]);
  const [audioLoading, setAudioLoading] = useState(role === "OWNER");
  const [cleanupPreview, setCleanupPreview] = useState<ApiRecord>();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [typeFilter, setTypeFilter] = useState("");
  const [editTarget, setEditTarget] = useState<ApiRecord>();
  const [editName, setEditName] = useState("");
  const [mediaPage, setMediaPage] = useState(1);
  const [totalMedia, setTotalMedia] = useState(0);
  const [totalMediaPages, setTotalMediaPages] = useState(1);
  const requestSequence = useRef(0);
  const localAddressUsageVisible = role === "OWNER" &&
    isLocalCheckoutHostname(window.location.hostname);
  const mapApiUsageVisible = role === "OWNER" && !localAddressUsageVisible;
  const [addressUsage, setAddressUsage] = useState<AddressSearchUsageSnapshot>();
  const [addressUsageError, setAddressUsageError] = useState<string>();
  const [mapApiUsage, setMapApiUsage] = useState<MapApiUsageSummary>();
  const [mapApiUsageError, setMapApiUsageError] = useState<string>();

  const loadAudioLibrary = useCallback(async () => {
    if (role !== "OWNER") return;
    setAudioLoading(true);
    try {
      const payload = await apiRequest<unknown>("/api/order-alerts/library");
      setAudioItems(normalizeCollection(payload).items);
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Suara notifikasi belum dapat dimuat.",
      );
    } finally {
      setAudioLoading(false);
    }
  }, [role]);

  const loadAddressUsage = useCallback(async () => {
    if (!localAddressUsageVisible) return;
    try {
      setAddressUsage(await apiRequest<AddressSearchUsageSnapshot>(
        "/api/owner/media/address-search-usage",
      ));
      setAddressUsageError(undefined);
    } catch (caught) {
      setAddressUsageError(caught instanceof Error
        ? caught.message
        : "Pemakaian pencarian alamat belum dapat dimuat.");
    }
  }, [localAddressUsageVisible]);

  const loadMapApiUsage = useCallback(async () => {
    if (!mapApiUsageVisible) return;
    try {
      setMapApiUsage(await apiRequest<MapApiUsageSummary>(
        "/api/owner/media/map-api-usage",
      ));
      setMapApiUsageError(undefined);
    } catch (caught) {
      setMapApiUsageError(caught instanceof Error
        ? caught.message
        : "Pemakaian API peta belum dapat dimuat.");
    }
  }, [mapApiUsageVisible]);

  const load = useCallback(async () => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    try {
      const payload = await apiRequest<unknown>(
        `${endpoint}${queryString({
          page: mediaPage,
          limit: MEDIA_PAGE_SIZE,
          search: debouncedSearch || undefined,
          status: statusFilter,
          type: typeFilter || undefined,
        })}`,
      );
      if (requestId !== requestSequence.current) return;
      const source = asRecord(payload);
      setItems(normalizeCollection(payload).items);
      setStorage(asRecord(get(source, "accountCapacity", "account_capacity")));
      setTotalMedia(asNumber(get(source, "total")));
      const nextTotalPages = Math.max(
        1,
        asNumber(get(source, "totalPages", "total_pages")),
      );
      setTotalMediaPages(nextTotalPages);
      if (mediaPage > nextTotalPages) setMediaPage(nextTotalPages);
      setError(undefined);
    } catch (caught) {
      if (requestId !== requestSequence.current) return;
      setError(
        caught instanceof Error ? caught.message : "Media belum dapat dimuat.",
      );
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [debouncedSearch, endpoint, mediaPage, statusFilter, typeFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  useEffect(() => {
    const timer = window.setTimeout(() => void loadAudioLibrary(), 0);
    return () => window.clearTimeout(timer);
  }, [loadAudioLibrary]);
  useEffect(() => {
    const timer = window.setTimeout(() => void loadAddressUsage(), 0);
    return () => window.clearTimeout(timer);
  }, [loadAddressUsage]);
  useEffect(() => {
    const timer = window.setTimeout(() => void loadMapApiUsage(), 0);
    return () => window.clearTimeout(timer);
  }, [loadMapApiUsage]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setMediaPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  const chooseFile = async (file?: File) => {
    if (!file) return;
    setNotice(undefined);
    try {
      const result = await optimizeProductImage(file);
      if (preview) URL.revokeObjectURL(preview);
      setOptimized(result);
      setPreview(URL.createObjectURL(result.blob));
    } catch (caught) {
      setNotice(
        caught instanceof Error ? caught.message : "Foto belum dapat diproses.",
      );
    }
  };

  const upload = async () => {
    if (!optimized || role !== "OWNER") return;
    setBusy(true);
    try {
      const dataBase64 = await blobToBase64(optimized.blob);
      await apiRequest("/api/media", {
        method: "POST",
        ...jsonBody({
          filename: optimized.filename,
          originalFilename: optimized.filename,
          mediaType,
          altText: optimized.filename.replace(/[-_]/gu, " "),
          dataBase64,
        }),
      });
      setNotice("Foto berhasil dioptimalkan dan diunggah.");
      setOptimized(undefined);
      if (preview) URL.revokeObjectURL(preview);
      setPreview(undefined);
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Foto belum berhasil diunggah.",
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget || role !== "OWNER") return;
    const id = asText(get(deleteTarget, "id", "mediaId", "media_id"), "");
    if (!id) return;
    setBusy(true);
    try {
      await apiRequest(`/api/owner/media/${encodeURIComponent(id)}`, {
        method: "DELETE",
        ...jsonBody({}),
      });
      setDeleteTarget(undefined);
      setNotice("Foto berhasil masuk masa tunggu 30 hari dan dapat dipulihkan.");
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Foto belum berhasil dihapus.",
      );
    } finally {
      setBusy(false);
    }
  };

  const restore = async (item: ApiRecord) => {
    if (role !== "OWNER") return;
    const id = asText(get(item, "id", "mediaId", "media_id"), "");
    if (!id) return;
    setBusy(true);
    try {
      await apiRequest(`/api/owner/media/${encodeURIComponent(id)}/restore`, {
        method: "POST",
        ...jsonBody({}),
      });
      setNotice("Foto berhasil dipulihkan dan tetap tidak digunakan.");
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error ? caught.message : "Foto belum dapat dipulihkan.",
      );
    } finally {
      setBusy(false);
    }
  };

  const previewFailedCleanup = async () => {
    if (role !== "OWNER") return;
    setBusy(true);
    setNotice(undefined);
    try {
      const payload = await apiRequest<unknown>(
        "/api/owner/media-cleanup/failed-preview",
      );
      setCleanupPreview(asRecord(payload));
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Dry-run upload gagal belum dapat dibuat.",
      );
    } finally {
      setBusy(false);
    }
  };

  const cleanupFailed = async () => {
    if (role !== "OWNER") return;
    setBusy(true);
    try {
      const payload = asRecord(
        await apiRequest<unknown>("/api/owner/media-cleanup/failed", {
          method: "POST",
          ...jsonBody({}),
        }),
      );
      setCleanupPreview(undefined);
      setNotice(
        `Pembersihan berhasil. ${asNumber(get(payload, "purged"))} catatan lama dibersihkan.`,
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Upload gagal belum dapat dibersihkan.",
      );
    } finally {
      setBusy(false);
    }
  };

  const removeAudio = async () => {
    if (!audioDeleteTarget || role !== "OWNER") return;
    const id = asText(get(audioDeleteTarget, "id"), "");
    if (!id) return;
    setBusy(true);
    try {
      await apiRequest(`/api/order-alerts/library/${encodeURIComponent(id)}`, {
        method: "DELETE",
        ...jsonBody({}),
      });
      setAudioDeleteTarget(undefined);
      setNotice("Suara notifikasi berhasil dihapus.");
      await loadAudioLibrary();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Suara notifikasi belum dapat dihapus.",
      );
    } finally {
      setBusy(false);
    }
  };

  const openRename = (item: ApiRecord) => {
    setEditTarget(item);
    setEditName(asText(get(item, "filename", "name"), ""));
  };

  const rename = async (event: FormEvent) => {
    event.preventDefault();
    const id = asText(get(editTarget ?? {}, "id", "mediaId", "media_id"), "");
    if (!id || editName.trim().length < 2) return;
    setBusy(true);
    setNotice(undefined);
    try {
      await apiRequest(`${endpoint}/${encodeURIComponent(id)}`, {
        method: "PATCH",
        ...jsonBody({ name: editName.trim() }),
      });
      setEditTarget(undefined);
      setNotice("Nama foto berhasil diubah.");
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error ? caught.message : "Nama foto belum berhasil diubah.",
      );
    } finally {
      setBusy(false);
    }
  };

  const slots = Array.isArray(storage?.slots)
    ? storage.slots.map(asRecord)
    : [];
  const configured = asNumber(
    get(storage ?? {}, "configuredDatabases", "configured_databases"),
  );
  const maximum = asNumber(
    get(storage ?? {}, "maximumDatabases", "maximum_databases"),
  );
  const remaining = asNumber(
    get(storage ?? {}, "remainingDatabaseSlots", "remaining_database_slots"),
  );

  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Penyimpanan gambar</span>
          <h1>{role === "OWNER" ? "Media" : "Katalog & Media"}</h1>
          <p>
            Foto produk dikirim dari database media dan dioptimalkan sebelum
            upload.
          </p>
        </div>
      </div>

      {notice && (
        <Notice tone={notice.includes("berhasil") ? "success" : "danger"}>
          {notice}
        </Notice>
      )}

      {localAddressUsageVisible && (
        <section className="dashboard-panel address-usage-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Shipping Lab lokal</span>
              <h2>Request pencarian alamat</h2>
              <p>Total otomatis kembali ke nol setelah satu siklus 30 hari.</p>
            </div>
            <MapPinned aria-hidden="true" />
          </div>
          {addressUsageError && <Notice tone="warning">{addressUsageError}</Notice>}
          {addressUsage && (
            <>
              <div className="address-usage-summary">
                <article><span>Hari ini</span><strong>{addressUsage.todayTotal}</strong></article>
                <article><span>Siklus 30 hari</span><strong>{addressUsage.cycleTotal}</strong></article>
                <article><span>Reset</span><strong>{addressUsage.daysRemaining} hari lagi</strong></article>
              </div>
              <div className="address-usage-days">
                {addressUsage.days.length === 0
                  ? <small>Belum ada request alamat pada siklus ini.</small>
                  : addressUsage.days.map((day) => (
                      <span key={day.date}>
                        <time dateTime={day.date}>{day.date}</time>
                        <strong>{day.total}</strong>
                      </span>
                    ))}
              </div>
            </>
          )}
        </section>
      )}

      {mapApiUsageVisible && (
        <section className="dashboard-panel address-usage-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Penggunaan ongkir</span>
              <h2>Request API peta</h2>
              <p>Hitungan harian dan bulanan kembali ke 0 saat periode berganti.</p>
            </div>
            <MapPinned aria-hidden="true" />
          </div>
          {mapApiUsageError && <Notice tone="warning">{mapApiUsageError}</Notice>}
          {mapApiUsage && (
            <div className="address-usage-summary">
              <article><span>Hari ini</span><strong>{mapApiUsage.todayTotal}</strong></article>
              <article><span>Bulan ini</span><strong>{mapApiUsage.monthTotal}</strong></article>
              <article><span>Sejak awal</span><strong>{mapApiUsage.allTimeTotal}</strong></article>
            </div>
          )}
        </section>
      )}

      {role === "OWNER" && storage && (
        <>
          <div className="media-summary">
            <article>
              <Database aria-hidden="true" />
              <span>Database akun</span>
              <strong>{configured}/{maximum} terpakai</strong>
              <small>{remaining} slot masih tersedia</small>
            </article>
            <article>
              <HardDrive aria-hidden="true" />
              <span>Database media</span>
              <strong>
                {asNumber(
                  get(
                    storage,
                    "configuredMediaDatabases",
                    "configured_media_databases",
                  ),
                )}
                /{asNumber(get(storage, "maximumMediaDatabases", "maximum_media_databases"))}
              </strong>
              <small>8 database media berikutnya tersedia</small>
            </article>
            <article>
              <span>Kapasitas per database</span>
              <strong>500 MB</strong>
              <small>Peringatan internal mulai 350 MB</small>
            </article>
            <article>
              <span>Kapasitas akun</span>
              <strong>5 GB</strong>
              <small>Gabungan seluruh database D1</small>
            </article>
          </div>
          <section className="dashboard-panel storage-map">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Peta 10 slot D1</span>
                <h2>Posisi database SedapSayur</h2>
              </div>
            </div>
            <div className="storage-slot-grid">
              {slots.map((slot) => {
                const status = asText(get(slot, "status"));
                const usage = get(slot, "usageBytes", "usage_bytes");
                return (
                  <article
                    className={`storage-slot storage-slot--${status.toLowerCase()}`}
                    key={asText(slot.slot)}
                  >
                    <small>Slot {asText(slot.slot)}</small>
                    <strong>{asText(slot.name)}</strong>
                    <span>{asText(slot.purpose)}</span>
                    <b>{status === "ACTIVE" ? "Aktif" : "Tersedia"}</b>
                    {typeof usage === "number" && status === "ACTIVE" && (
                      <em>{megabytes(usage)} digunakan</em>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        </>
      )}

      {role === "OWNER" && (
        <section className="media-uploader">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Upload aman</span>
              <h2>Tambah Foto</h2>
            </div>
          </div>
          <div className="media-upload-grid">
            <label className="drop-zone">
              <Upload aria-hidden="true" />
              <strong>Pilih foto produk</strong>
              <span>JPG, PNG, atau WebP. Maksimal 10 MB sebelum diproses.</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => void chooseFile(event.target.files?.[0])}
              />
            </label>
            {optimized && preview && (
              <div className="optimization-preview">
                <img src={preview} alt="Preview foto setelah dioptimalkan" />
                <div>
                  <strong>{optimized.filename}</strong>
                  <dl>
                    <div>
                      <dt>Foto asli</dt>
                      <dd>{(optimized.originalBytes / 1024).toFixed(0)} KB</dd>
                    </div>
                    <div>
                      <dt>Hasil WebP</dt>
                      <dd>{(optimized.optimizedBytes / 1024).toFixed(0)} KB</dd>
                    </div>
                    <div>
                      <dt>Resolusi</dt>
                      <dd>800 × 800</dd>
                    </div>
                    <div>
                      <dt>Penghematan</dt>
                      <dd>{optimized.savingPercent}%</dd>
                    </div>
                  </dl>
                  <label>
                    Jenis media
                    <select
                      value={mediaType}
                      onChange={(event) => setMediaType(event.target.value)}
                    >
                      <option value="product">Produk</option>
                      <option value="category">Kategori</option>
                      <option value="banner">Banner</option>
                      <option value="promo">Promo</option>
                    </select>
                  </label>
                  <Button onClick={() => void upload()} busy={busy}>
                    Upload Foto
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {role === "OWNER" && (
        <section className="dashboard-panel audio-library">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Audio cabang</span>
              <h2>Suara notifikasi tersimpan</h2>
              <p>
                Menampilkan suara aktif dan suara lama yang sudah diganti. File
                dapat didengarkan sebelum dihapus.
              </p>
            </div>
          </div>
          {audioLoading && <Skeleton rows={2} cards />}
          {!audioLoading && audioItems.length === 0 && (
            <EmptyState
              title="Belum ada suara tersimpan"
              description="Suara yang diunggah dari pengaturan alarm cabang akan tampil di sini."
            />
          )}
          {!audioLoading && audioItems.length > 0 && (
            <div className="audio-library-grid">
              {audioItems.map((item) => {
                const id = asText(get(item, "id"), "");
                const hasBlob = Boolean(get(item, "has_blob", "hasBlob"));
                const isCurrent = Boolean(get(item, "is_current", "isCurrent"));
                const enabled = Boolean(
                  get(item, "branch_alert_enabled", "branchAlertEnabled"),
                );
                return (
                  <article className="audio-library-card" key={id}>
                    <div className="audio-library-card__heading">
                      <Music2 aria-hidden="true" />
                      <div>
                        <strong>{asText(get(item, "original_filename"))}</strong>
                        <span>{asText(get(item, "branch_name"))}</span>
                      </div>
                    </div>
                    <small>
                      {(asNumber(get(item, "physical_size_bytes", "size_bytes")) / 1024).toFixed(0)} KB
                      {isCurrent
                        ? enabled
                          ? " · Aktif dipakai cabang"
                          : " · Dipilih, alarm dimatikan"
                        : " · Suara lama / telah diganti"}
                    </small>
                    {hasBlob ? (
                      <audio
                        controls
                        preload="none"
                        src={`/api/order-alerts/library/${encodeURIComponent(id)}/content`}
                      >
                        Browser tidak mendukung pemutar audio.
                      </audio>
                    ) : (
                      <Notice tone="warning">File audio tidak ditemukan.</Notice>
                    )}
                    <Button
                      variant="danger"
                      disabled={busy}
                      onClick={() => setAudioDeleteTarget(item)}
                      icon={<Trash2 aria-hidden="true" />}
                    >
                      Hapus Suara
                    </Button>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}

      <label className="media-search">
        <Search aria-hidden="true" />
        <span className="sr-only">Cari nama foto atau produk</span>
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          placeholder="Cari nama foto atau produk…"
        />
      </label>
      <div className="toolbar media-library-filters">
        <label className="toolbar-filter">
          Status foto
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setMediaPage(1);
            }}
          >
            <option value="active">Foto aktif</option>
            <option value="used">Sedang digunakan</option>
            <option value="unused">Tidak digunakan</option>
            <option value="failed">Upload gagal</option>
            {role === "OWNER" && (
              <option value="deleting">Menunggu dihapus</option>
            )}
          </select>
        </label>
        <label className="toolbar-filter">
          Jenis foto
          <select
            value={typeFilter}
            onChange={(event) => {
              setTypeFilter(event.target.value);
              setMediaPage(1);
            }}
          >
            <option value="">Semua jenis</option>
            <option value="product">Produk</option>
            <option value="category">Kategori</option>
            <option value="banner">Banner</option>
            <option value="promo">Promo</option>
            <option value="other">Lainnya</option>
          </select>
        </label>
        <span className="media-library-count">
          {totalMedia > 0
            ? `${(mediaPage - 1) * MEDIA_PAGE_SIZE + 1}–${Math.min(mediaPage * MEDIA_PAGE_SIZE, totalMedia)} dari ${totalMedia} foto`
            : "0 foto"}
        </span>
        {role === "OWNER" && statusFilter === "failed" && (
          <Button
            variant="danger"
            busy={busy}
            onClick={() => void previewFailedCleanup()}
            icon={<Trash2 aria-hidden="true" />}
          >
            Bersihkan Upload Gagal
          </Button>
        )}
      </div>

      {loading && <Skeleton rows={6} cards />}
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!loading && !error && items.length === 0 && (
        <EmptyState
          title={debouncedSearch ? "Foto tidak ditemukan" : "Belum ada media"}
          description={debouncedSearch ? "Coba nama foto atau produk lain." : "Upload foto pertama untuk produk atau kategori."}
        />
      )}
      {!loading && !error && items.length > 0 && (
        <div className="media-gallery">
          {items.map((item, index) => {
            const mediaId = get(
              item,
              "mediaId",
              "media_id",
              "branchMediaId",
              "branch_media_id",
              "id",
            ) as string | number | undefined;
            const referenceCount = asNumber(
              get(
                item,
                "actualRefCount",
                "actual_ref_count",
                "publicRefCount",
                "public_ref_count",
              ),
            );
            const lifecycleStatus = asText(
              get(item, "lifecycleStatus", "lifecycle_status"),
              "active",
            );
            const canRename =
              lifecycleStatus === "active" &&
              (role === "OWNER" || Boolean(get(item, "branchId", "branch_id")));
            const isFailed = lifecycleStatus === "failed";
            const isDeleting = lifecycleStatus === "deleting";
            const physicalBlobExists = Boolean(
              get(item, "physical_blob_exists", "physicalBlobExists"),
            );
            return (
              <article key={`${String(mediaId)}-${index}`}>
                {lifecycleStatus === "active" && mediaId ? (
                  <img
                    src={`/api/media/private/${encodeURIComponent(String(mediaId))}`}
                    alt={asText(get(item, "filename", "name"))}
                    loading="lazy"
                    width="800"
                    height="800"
                  />
                ) : (
                  <span className="media-failed-placeholder">
                    {isDeleting ? <Clock3 aria-hidden="true" /> : <ImageOff aria-hidden="true" />}
                    {isDeleting ? "Menunggu dihapus" : "Foto upload gagal"}
                  </span>
                )}
                <div>
                  <strong>{asText(get(item, "filename", "name"))}</strong>
                  <span>
                    {asText(
                      get(
                        item,
                        "mediaType",
                        "media_type",
                        "categoryName",
                        "category_name",
                      ),
                    )}
                  </span>
                  <small>
                    {asNumber(get(item, "sizeBytes", "size_bytes"))
                      ? `${isFailed ? "Ukuran percobaan: " : ""}${(
                          asNumber(get(item, "sizeBytes", "size_bytes")) / 1024
                        ).toFixed(0)} KB`
                      : "Foto produk"}
                  </small>
                  {isFailed && (
                    <>
                      <small className="media-status-badge">Upload gagal</small>
                      <small className="media-capacity-note">
                        {physicalBlobExists
                          ? "File sisa terdeteksi—akan dihitung dalam dry-run"
                          : "File tidak tersimpan—tidak memakai kapasitas media"}
                      </small>
                    </>
                  )}
                  {isDeleting && (
                    <>
                      <small className="media-status-badge">Masa tunggu 30 hari</small>
                      <small>
                        Dihapus setelah {asText(get(item, "delete_after", "deleteAfter"))}
                      </small>
                    </>
                  )}
                  {(canRename || (role === "OWNER" && isDeleting)) && (
                    <div className="media-gallery__actions">
                      {role === "OWNER" && lifecycleStatus === "active" && (
                        <small>
                          {referenceCount > 0
                            ? `Dipakai di ${referenceCount} tempat`
                            : "Tidak sedang digunakan"}
                        </small>
                      )}
                      {canRename && (
                        <Button
                          variant="secondary"
                          disabled={busy}
                          onClick={() => openRename(item)}
                          icon={<Pencil aria-hidden="true" />}
                        >
                          Ubah Nama
                        </Button>
                      )}
                      {role === "OWNER" && lifecycleStatus === "active" && (
                        <Button
                          variant="danger"
                          disabled={referenceCount > 0 || busy}
                          onClick={() => setDeleteTarget(item)}
                          icon={<Trash2 aria-hidden="true" />}
                        >
                          Hapus Foto
                        </Button>
                      )}
                      {role === "OWNER" && isDeleting && (
                        <Button
                          variant="secondary"
                          disabled={busy}
                          onClick={() => void restore(item)}
                          icon={<RotateCcw aria-hidden="true" />}
                        >
                          Pulihkan Foto
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
      {!loading && !error && totalMediaPages > 1 && (
        <nav className="catalog-pagination" aria-label="Halaman media">
          <button
            type="button"
            disabled={mediaPage <= 1}
            onClick={() => setMediaPage((current) => Math.max(1, current - 1))}
          >
            Sebelumnya
          </button>
          <span>
            Halaman {mediaPage} dari {totalMediaPages}
          </span>
          <button
            type="button"
            disabled={mediaPage >= totalMediaPages}
            onClick={() => setMediaPage((current) => current + 1)}
          >
            Berikutnya (50 foto)
          </button>
        </nav>
      )}
      <Modal
        open={Boolean(editTarget)}
        onClose={() => {
          if (!busy) setEditTarget(undefined);
        }}
        title="Ubah nama foto"
        description="Nama ini digunakan pada pencarian gambar produk."
      >
        <form className="modal-form" onSubmit={(event) => void rename(event)}>
          <label>
            Nama foto / produk
            <input
              required
              autoFocus
              minLength={2}
              maxLength={140}
              value={editName}
              onChange={(event) => setEditName(event.target.value)}
            />
          </label>
          <div className="modal-actions">
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setEditTarget(undefined)}>
              Batal
            </Button>
            <Button type="submit" busy={busy}>Simpan Nama</Button>
          </div>
        </form>
      </Modal>
      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!busy) setDeleteTarget(undefined);
        }}
        title="Masukkan ke masa tunggu 30 hari?"
        description="Foto harus tidak dipakai produk, kategori, banner, atau pembayaran. File disembunyikan sekarang, dapat dipulihkan selama 30 hari, lalu dihapus otomatis."
      >
        <div className="modal-actions">
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => setDeleteTarget(undefined)}
          >
            Batal
          </Button>
          <Button variant="danger" busy={busy} onClick={() => void remove()}>
            Jadwalkan Hapus
          </Button>
        </div>
      </Modal>
      <Modal
        open={Boolean(cleanupPreview)}
        onClose={() => {
          if (!busy) setCleanupPreview(undefined);
        }}
        title="Dry-run Bersihkan Upload Gagal"
        description="Belum ada data yang dihapus. Hanya catatan gagal berumur minimal 30 hari dan tanpa referensi yang dapat dibersihkan."
      >
        <div className="media-retention-summary">
          <span>
            <small>Catatan gagal</small>
            <strong>{asNumber(get(cleanupPreview ?? {}, "failedRecords"))}</strong>
          </span>
          <span>
            <small>Memenuhi umur 30 hari</small>
            <strong>{asNumber(get(cleanupPreview ?? {}, "eligibleRecords"))}</strong>
          </span>
          <span>
            <small>Dapat dibersihkan</small>
            <strong>{asNumber(get(cleanupPreview ?? {}, "cleanableRecords"))}</strong>
          </span>
          <span>
            <small>Blob nyata</small>
            <strong>{asNumber(get(cleanupPreview ?? {}, "physicalBlobs"))}</strong>
          </span>
          <span>
            <small>Total byte blob nyata</small>
            <strong>{megabytes(asNumber(get(cleanupPreview ?? {}, "physicalBlobBytes")))}</strong>
          </span>
          <span>
            <small>Jumlah referensi</small>
            <strong>{asNumber(get(cleanupPreview ?? {}, "referenceCount"))}</strong>
          </span>
        </div>
        <Notice tone="info">
          Ukuran percobaan tidak dihitung sebagai kapasitas media bila blob nyata tidak ada.
        </Notice>
        <div className="modal-actions">
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => setCleanupPreview(undefined)}
          >
            Batal
          </Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={
              asNumber(get(cleanupPreview ?? {}, "cleanableRecords")) === 0
            }
            onClick={() => void cleanupFailed()}
          >
            Bersihkan yang Memenuhi Syarat
          </Button>
        </div>
      </Modal>
      <Modal
        open={Boolean(audioDeleteTarget)}
        onClose={() => {
          if (!busy) setAudioDeleteTarget(undefined);
        }}
        title="Hapus suara notifikasi?"
        description={
          get(audioDeleteTarget ?? {}, "is_current", "isCurrent")
            ? "Suara ini sedang dipilih cabang. Menghapusnya akan mematikan alarm cabang sampai Owner mengunggah suara baru."
            : "Suara lama akan dikeluarkan dari penyimpanan media."
        }
      >
        <div className="modal-actions">
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => setAudioDeleteTarget(undefined)}
          >
            Batal
          </Button>
          <Button variant="danger" busy={busy} onClick={() => void removeAudio()}>
            Hapus Suara
          </Button>
        </div>
      </Modal>
    </div>
  );
}
